import type { AssembledContract, Block } from "../contracts/types";
import { amountToWords } from "../contracts/amountToWords";
import { CONTRACT_KIND_LABEL, isAdvanceCert, certPaid, certOutstanding, certPayState, PAY_STATE_LABEL, hasValuation, computeBuildup, type Contract, type PaymentCert, type PaymentProject } from "./types";

function money(amount: number, currency: string): string {
  if (amount == null || !isFinite(amount)) return `${currency} ____________`;
  return `${currency} ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Classify the certificate so it carries the correct FIDIC basis and title. */
function classify(contract: Contract, cert: PaymentCert) {
  const isSupply = cert.kind === "material-supply";
  const isServices = cert.kind === "services";
  // Same rule as the ledger aggregates (isAdvanceCert): a construction
  // certificate whose description says "advance".
  if (isAdvanceCert(cert)) return { key: "advance" as const, title: "Advance Payment Certificate", subClause: "14.2", subClauseName: "Advance Payment" };
  if (isSupply) return { key: "supply" as const, title: "Supply Payment Certificate", subClause: "", subClauseName: "" };
  if (isServices) return { key: "services" as const, title: "Payment Certificate", subClause: "", subClauseName: "" };
  if (cert.kind === "other") return { key: "services" as const, title: "Payment Certificate", subClause: "", subClauseName: "" };
  // A final certificate is built up like an interim one (cumulative to date)
  // but is titled as the Final Payment Certificate (Sub-Clause 14.13).
  if (/\bfinal\b/i.test(cert.description || "") || /\bfinal\b/i.test(cert.number || "")) return { key: "interim" as const, title: "Final Payment Certificate", subClause: "14.13", subClauseName: "Issue of Final Payment Certificate" };
  return { key: "interim" as const, title: "Interim Payment Certificate", subClause: "14.6", subClauseName: "Issue of the Interim Payment Certificate" };
}

const FIDIC_LINE = "the FIDIC Conditions of Contract for Construction, Second Edition 2017";

export function assembleCertificate(project: PaymentProject, contract: Contract, cert: PaymentCert): AssembledContract {
  const ccy = cert.currency || contract.currency || project.currency;
  const words = amountToWords({ amount: cert.amount, currency: ccy });
  const cls = classify(contract, cert);
  const isFidic = cls.key === "interim" || cls.key === "advance";
  const isSupply = cls.key === "supply";

  // Cumulative build-up to and including this certificate, computed WITHIN the
  // same category (advances vs works) so a repayable advance is never mixed with
  // certified works value. Advances carry no retention and do not consume value.
  const idx = contract.certificates.findIndex((c) => c.id === cert.id);
  const upto = idx >= 0 ? contract.certificates.slice(0, idx + 1) : contract.certificates;
  const amt = isFinite(cert.amount) ? cert.amount : 0;
  const sameCat = upto.filter((c) => isAdvanceCert(c) === (cls.key === "advance"));
  const cumulative = sameCat.reduce((s, c) => s + (isFinite(c.amount) ? c.amount : 0), 0);
  const previously = cumulative - amt;
  const retPct = contract.retentionPercent || 0;
  // Retention applies to certified WORKS only — never to advances.
  const worksCumulative = cls.key === "advance" ? 0 : cumulative;
  const retentionHeld = (retPct / 100) * worksCumulative;
  const value = contract.value != null && isFinite(contract.value) ? contract.value : null;
  // Advance is repayable → it does NOT reduce the contract balance remaining.
  const remaining = value != null && cls.key !== "advance" ? value - cumulative : null;

  const payee = contract.party || "the Payee";
  const supplierName = contract.supplier || contract.party || "the Supplier";
  const bu = cert.buildup;
  // An interim/final certificate that carries the Contractor's Statement
  // valuation prints the full Clause 14 build-up; one with a plain amount
  // prints the simple cumulative summary.
  const detailed = cls.key === "interim" && hasValuation(bu);
  const n0 = (v?: number) => (v == null || !isFinite(v) ? 0 : v);
  const neg = (x: number) => (x ? `(${money(Math.abs(x), ccy)})` : money(0, ccy));

  // --- Header ---------------------------------------------------------------
  const header: Block = {
    type: "table",
    columns: ["Field", "Detail"],
    rows: [
      ["Project", `${project.name}${project.reference ? ` (${project.reference})` : ""}`],
      ["Contract", `${contract.name} — ${CONTRACT_KIND_LABEL[contract.kind]}${contract.reference ? ` (${contract.reference})` : ""}`],
      [isSupply ? "Supplier / Payee" : "Contractor / Payee", isSupply ? supplierName : payee],
      ...(project.authoriser ? [["Employer / Certifying party", `${project.authoriser}${project.authoriserTitle ? `, ${project.authoriserTitle}` : ""}`] as string[]] : []),
      ["Certificate No.", cert.number || "—"],
      ["Certificate date", cert.date || "—"],
      ["Currency", ccy],
      ...(isSupply && cert.invoiceRef ? [["Supplier invoice", `${cert.invoiceRef}${cert.invoiceDate ? ` dated ${cert.invoiceDate}` : ""}`] as string[]] : []),
      ...(cert.invoice ? [[isSupply ? "Supplier's invoice (attached)" : "Contractor's invoice / statement (attached)", cert.invoice.name] as string[]] : []),
      ...(isFidic && bu?.engineerName ? [["Engineer", bu.engineerName] as string[]] : []),
      ...(isFidic && (bu?.statementRef || bu?.statementDate) ? [["Contractor's Statement (14.3)", `${bu.statementRef || "—"}${bu.statementDate ? ` dated ${bu.statementDate}` : ""}`] as string[]] : []),
      ...(isFidic && (bu?.periodFrom || bu?.periodTo) ? [["Payment period", `${bu.periodFrom || "—"} to ${bu.periodTo || "—"}`] as string[]] : []),
    ],
  };

  const intro = isFidic
    ? `Issued under Sub-Clause ${cls.subClause} [${cls.subClauseName}], Clause 14 [Contract Price and Payment] of ${FIDIC_LINE}.`
    : isSupply
      ? `This Certificate authorises payment for the supply of materials or goods against the supplier's invoice identified below.`
      : `This Certificate authorises payment for services rendered under the Contract identified below.`;

  const blocks: Block[] = [
    { type: "heading", text: `Certificate of Payment — ${cls.title}`, level: 1 },
    { type: "paragraph", text: intro },
    header,
  ];

  // --- Payment build-up -----------------------------------------------------
  const buildup: string[][] = [];
  if (detailed) {
    const s = computeBuildup(contract, cert);
    const retPctUsed = bu?.retentionPercent ?? contract.retentionPercent ?? 0;
    blocks.push(
      { type: "heading", text: "A. Valuation of the Works to date (Sub-Clause 14.3)", level: 2 },
      {
        type: "table",
        columns: ["Item", "Sub-Clause", `Amount (${ccy})`],
        rows: [
          ["Value of the permanent Works executed", "14.3(a)", money(n0(bu?.worksExecuted), ccy)],
          ["Plant and Materials", "14.5", money(n0(bu?.materialsOnSite), ccy)],
          ["Variations", "13.3", money(n0(bu?.variations), ccy)],
          ["Provisional Sums", "13.4", money(n0(bu?.provisionalSums), ccy)],
          ["Dayworks", "13.5", money(n0(bu?.dayworks), ccy)],
          ["Adjustments (legislation / cost)", "13.6 / 13.7", money(n0(bu?.priceAdjustment), ccy)],
          ["Other additions (claims, etc.)", "—", money(n0(bu?.otherAdditions), ccy)],
          ["Gross value of work done to date", "", money(s.gross, ccy)],
        ],
      },
      { type: "heading", text: "B. Retention (Sub-Clause 14.3 / 14.9)", level: 2 },
      {
        type: "table",
        columns: ["Item", `Amount (${ccy})`],
        rows: [
          [`Retention at ${retPctUsed}%${bu?.retentionLimitPercent != null && isFinite(bu.retentionLimitPercent) ? `, limited to ${bu.retentionLimitPercent}% of the Accepted Contract Amount` : ""}`, money(s.retentionGross, ccy)],
          ["Less retention released (14.9)", money(n0(bu?.retentionReleased), ccy)],
          ["Net retention held to date", money(s.retentionHeld, ccy)],
        ],
      },
      { type: "heading", text: `Payment Summary (Sub-Clause ${cls.subClause})`, level: 2 },
      {
        type: "table",
        columns: ["Description", `Amount (${ccy})`],
        rows: [
          ["Gross value of work done to date", money(s.gross, ccy)],
          ["Less: retention held to date", neg(s.retentionHeld)],
          ["Less: advance payment recovered to date (14.2.3)", neg(s.advanceRecovered)],
          ["Net value certified to date", money(s.netToDate, ccy)],
          ["Less: amounts certified in previous certificates (14.3(g))", neg(s.previouslyCertified)],
          ["Amount due this Certificate (excl. VAT)", money(s.dueThisCert, ccy)],
          ...(bu?.vatPercent ? ([[`Add: VAT at ${bu.vatPercent}%`, money(s.vat, ccy)]] as string[][]) : []),
          [`Total payable this Certificate${bu?.vatPercent ? " (incl. VAT)" : " (excl. VAT)"}`, money(s.totalPayable, ccy)],
          ...(value != null ? ([["Accepted Contract Amount (for reference)", money(value, ccy)], ["Balance of Contract Price not yet certified (gross)", money(value - s.gross, ccy)]] as string[][]) : []),
        ],
      },
    );
    if (bu?.paymentDueDays) blocks.push({ type: "paragraph", text: `Payment shall be made by the Employer within ${bu.paymentDueDays} days in accordance with Sub-Clause 14.7 [Payment].` });
  } else blocks.push({ type: "heading", text: isFidic ? `Payment Summary (Sub-Clause ${cls.subClause})` : "Payment Summary", level: 2 });
  if (detailed) {
    /* rendered above */
  } else if (cls.key === "advance") {
    if (value != null) buildup.push(["Accepted Contract Amount (for reference)", money(value, ccy)]);
    if (bu?.advancePercent != null && isFinite(bu.advancePercent)) buildup.push(["Total advance payment under the Contract", `${bu.advancePercent}% of the Accepted Contract Amount${value != null ? ` — ${money((bu.advancePercent / 100) * value, ccy)}` : ""}`]);
    if (bu?.installmentIndex) buildup.push(["This installment", `No. ${bu.installmentIndex} of ${bu.installmentCount || "__"}`]);
    buildup.push(["Advance Payment Guarantee (14.2.1)", bu?.guaranteeRef || "On file"]);
    buildup.push(["Value of the Works executed and certified by this Certificate", money(0, ccy)]);
    buildup.push(["Advance Payment certified by this Certificate (Sub-Clause 14.2)", money(amt, ccy)]);
    if (previously > 0.005) buildup.push(["Advance previously paid under this Contract", money(previously, ccy)]);
    buildup.push([`Total advance paid to date${previously > 0.005 ? " (incl. this Certificate)" : ""}`, money(cumulative, ccy)]);
    buildup.push(["Retention deducted from this advance (Sub-Clause 14.3)", "Nil — no retention applies to advance payments"]);
    buildup.push(["Recovery of the advance", "By deductions from later IPCs (Sub-Clause 14.2.3)"]);
    blocks.push({ type: "table", columns: ["Description", `Amount (${ccy})`], rows: buildup });
    blocks.push({ type: "paragraph", text: `The Advance Payment is repayable. It does not represent value of the Works, does not reduce the Contract Price or the balance outstanding to be certified, and carries no retention. It is recovered by deductions from subsequent Interim Payment Certificates under Sub-Clause 14.2.3, and shall be fully recovered before issue of the Taking-Over Certificate.` });
  } else {
    if (isSupply) {
      buildup.push([`Value of materials / goods supplied — ${cert.description || "as described"}${cert.invoiceRef ? ` (Invoice ${cert.invoiceRef})` : ""}`, money(amt, ccy)]);
    } else {
      buildup.push([`Amount certified by this Certificate — ${cert.description || "works executed"}`, money(amt, ccy)]);
    }
    if (previously > 0.005) buildup.push(["Less: value previously certified under this Contract", money(previously, ccy)]);
    buildup.push([`Total value certified to date under this Contract${previously > 0.005 ? " (incl. this Certificate)" : ""}`, money(cumulative, ccy)]);
    if (retPct > 0 && isFidic) buildup.push([`Retention held to date, at ${retPct}% of certified Works (Sub-Clause 14.3 / 14.9)`, money(retentionHeld, ccy)]);
    if (value != null) {
      buildup.push(["Contract value", money(value, ccy)]);
      buildup.push(["Balance of contract value remaining", money(remaining as number, ccy)]);
    }
    blocks.push({ type: "table", columns: ["Description", `Amount (${ccy})`], rows: buildup });
  }

  // --- Certification statement ----------------------------------------------
  const amountPhrase = `${words ? words + " — " : ""}${money(amt, ccy)}`;
  if (cls.key === "advance") {
    blocks.push({ type: "heading", text: "Certification", level: 2 });
    blocks.push({ type: "paragraph", text: `The undersigned, for and on behalf of the Employer, hereby certifies that the sum of ${amountPhrase} is due and payable to ${payee} as an advance (mobilisation) payment under Sub-Clause 14.2 [Advance Payment] of ${FIDIC_LINE}. No value of the Works executed is certified by this Certificate; the entitlement arises solely from the Advance Payment provision and is secured by the Advance Payment Guarantee under Sub-Clause 14.2.1.` });
    blocks.push({ type: "paragraph", text: `The advance payment shall be recovered by deductions from subsequent Interim Payment Certificates in accordance with Sub-Clause 14.2.3, and shall be fully recovered before issue of the Taking-Over Certificate. Payment shall be made by the Employer in accordance with Sub-Clause 14.7 [Payment].` });
  } else if (cls.key === "interim") {
    blocks.push({ type: "heading", text: "Certification", level: 2 });
    blocks.push({ type: "paragraph", text: `The undersigned, for and on behalf of the Employer, hereby certifies that the sum of ${amountPhrase} is due and payable to ${payee} in respect of ${cert.description || "the Works executed"} under Sub-Clause 14.6 [Issue of the Interim Payment Certificate] of ${FIDIC_LINE}. Payment shall be made by the Employer in accordance with Sub-Clause 14.7 [Payment]. This Certificate is exclusive of any Value Added Tax, which shall be added in accordance with the prevailing tax legislation at the time of invoicing.` });
  } else {
    blocks.push({ type: "heading", text: "Authorisation of Payment", level: 2 });
    blocks.push({ type: "paragraph", text: `Payment of ${amountPhrase} is hereby authorised in favour of ${isSupply ? supplierName : payee} in settlement of ${isSupply ? `the supply of ${cert.description || "materials / goods"}${cert.invoiceRef ? ` against Invoice No. ${cert.invoiceRef}${cert.invoiceDate ? ` dated ${cert.invoiceDate}` : ""}` : ""}` : cert.description || "the services rendered"}, under the ${contract.name}${contract.reference ? ` (${contract.reference})` : ""} for the Project. This Certificate is exclusive of taxes unless otherwise stated.` });
  }

  // --- Payment status (derived from the payments logged against this cert) ---
  // Only shown once something has actually been received, so an unpaid certificate
  // reads exactly as it always did.
  const paid = certPaid(cert);
  if (paid > 0.005) {
    const state = certPayState(cert);
    const out = certOutstanding(cert);
    blocks.push({ type: "heading", text: "Payment Status", level: 2 });
    blocks.push({
      type: "table",
      columns: ["", ""],
      rows: [
        ["Status", PAY_STATE_LABEL[state]],
        ["Amount certified", money(cert.amount, ccy)],
        ["Received to date", money(paid, ccy)],
        ...(state === "paid" ? [] : [["Outstanding", money(out, ccy)] as string[]]),
      ],
    });
    const lines = (cert.payments || []).filter((p) => Number.isFinite(p.amount) && p.amount > 0);
    if (lines.length) {
      blocks.push({
        type: "table",
        columns: ["#", "Date", "Amount", "Method / reference"],
        rows: lines.map((p, i) => [String(i + 1), p.date || "—", money(p.amount, ccy), p.ref || "—"]),
      });
    }
    blocks.push({
      type: "paragraph",
      text: state === "paid"
        ? `This Certificate has been settled in full as at the date of this document.`
        : `${money(out, ccy)} remains outstanding against this Certificate as at the date of this document.`,
    });
  }

  // --- Settlement to date ---------------------------------------------------
  // Stated as the certificate's effect (the Contractor does not countersign the
  // certificate, so this is not a payee-signed acknowledgement).
  if (cert.coversAllOutstanding) {
    blocks.push({ type: "heading", text: "Settlement to Date", level: 2 });
    blocks.push({
      type: "paragraph",
      text: `This Certificate covers all amounts due to ${payee} under the ${contract.name} up to its date. Upon payment of this Certificate, all sums due and outstanding to ${payee} up to the date of this Certificate — whether for works executed, materials or goods supplied, or otherwise — will have been paid and satisfied in full${cert.reserved ? ", save for the amounts expressly reserved below" : ""}.`,
    });
    if (cert.reserved) blocks.push({ type: "paragraph", text: `Amounts expressly reserved / not settled by this Certificate: ${cert.reserved}.` });
  } else if (cert.reserved) {
    blocks.push({ type: "paragraph", text: `Amounts expressly reserved / not settled by this Certificate: ${cert.reserved}.` });
  }

  if (cert.notes) blocks.push({ type: "paragraph", text: cert.notes });

  // --- Signature ------------------------------------------------------------
  // The certificate is issued and signed by the certifying party (us) only — it
  // is a certification, not an agreement, so the Contractor / payee does not
  // countersign. (Their invoice is provided separately, e.g. attached against the
  // certificate's share link.)
  blocks.push({
    type: "signblock",
    parties: [
      { role: isFidic ? "Certified by (for and on behalf of the Employer)" : "Authorised by (for the Employer / Project)", name: cert.authorisedBy || project.authoriser, title: project.authoriserTitle, signatureDataUri: cert.authorisedSignature, signedDate: cert.authorisedDate },
    ],
  });

  const notice = isFidic
    ? `Certificate of Payment issued under Clause 14 [Contract Price and Payment] of ${FIDIC_LINE}. Amounts are exclusive of Value Added Tax unless stated. Payment is to be made in accordance with Sub-Clause 14.7 [Payment].`
    : `Payment authorisation certificate. Amounts exclusive of taxes unless stated. This Certificate records the Employer's authorisation of the payment shown.`;

  return {
    title: `${project.name} — ${cls.title} ${cert.number || ""}`.trim(),
    standardLabel: isFidic ? "FIDIC 2017 — Certificate of Payment" : "Payment Authorisation Certificate",
    masterCover: {
      kicker: cls.key === "advance" ? "Payment · Advance (14.2)" : cls.key === "interim" ? "Payment · Interim (14.6)" : isSupply ? "Payment · Material Supply" : "Payment · Services",
      title: "Certificate of Payment",
      subtitle: project.name,
      contractLabel: cls.title.toUpperCase(),
      standardLine: `${contract.name}${isFidic ? ` — ${FIDIC_LINE}` : ""}`,
      date: cert.date,
      meta: [
        { label: "Certificate No.", value: cert.number || "—" },
        { label: isSupply ? "Supplier / Payee" : "Payee", value: isSupply ? supplierName : payee },
        ...(isSupply && cert.invoiceRef ? [{ label: "Supplier invoice", value: cert.invoiceRef }] : []),
        { label: "Amount", value: money(amt, ccy) },
      ],
    },
    parts: [{ id: "cert", name: cls.title, cover: { kicker: "Certificate", title: "Certificate of Payment", subtitle: `${cls.title} — ${cert.number || ""}` }, blocks }],
    notice,
  };
}
